import {
  FlexBox,
  JoonColorExpanded,
  SPACING,
  Loader,
  ChatIcon,
  JoonUIColor,
  Typography,
  BanIcon,
  CalendarCheckIcon,
  CheckIcon,
} from "@joonapp/web-shared"
import {
  SessionBillingPeriod,
  SessionBooking,
} from "@joonapp/web-shared/dist/types/sessions"
import { CellContext } from "@tanstack/react-table"
import dayjs from "dayjs"
import { useId, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Tooltip } from "react-tooltip"

import { useGetAllBillingPeriodsQuery } from "../../../networking/sessions/queries"
import { CoachingGroup } from "../../../types"
import { ClientTableData } from "../../../types/misc"
import { ClientTab } from "../../clients/ClientTabNav"
import useSessionModalStore from "../../clients/sessions/hooks/useSessionModalStore"

const SessionsCell = (
  info: CellContext<ClientTableData, SessionBillingPeriod[] | null>
) => {
  const { isLoading } = useGetAllBillingPeriodsQuery()
  const billingPeriods = info.getValue()

  const hasNoBookings = billingPeriods?.every(
    (period) => period.bookings.length === 0
  )
  const userHasDowngraded =
    hasNoBookings && billingPeriods?.some((period) => period.max_bookings === 0)

  if (isLoading)
    return <Loader size={12} thickness={2} color={JoonUIColor.icon.neutral} />
  if (!billingPeriods) return "N/A"
  if (billingPeriods.length === 0 || userHasDowngraded)
    return (
      <FlexBox
        direction="row"
        gap={SPACING.space1}
        align="center"
        wrap={false}
        style={{
          background: JoonUIColor.background.lightGray,
          borderRadius: SPACING.space1,
          width: "fit-content",
          padding: `${SPACING.space1} ${SPACING.space2}`,
        }}
      >
        <ChatIcon color={JoonColorExpanded.purple600} size={12} />
        <Typography variant="bodyXSmall" color={JoonColorExpanded.purple600}>
          Messaging plan
        </Typography>
      </FlexBox>
    )

  const makeupSessions = billingPeriods.flatMap((period) =>
    period.bookings.filter((b) => b.missed_session_id)
  )

  return (
    <FlexBox direction="row" gap={SPACING.space05} align="center">
      {billingPeriods.map((period, i) => {
        const sessionsMinusMakeups = period.bookings.filter(
          (booking) => !booking.missed_session_id
        )
        const numSlotsToShow = Math.max(
          period.max_bookings,
          sessionsMinusMakeups.length
        )

        return (
          <>
            {Array.from({ length: numSlotsToShow }).map((_, index) => {
              const booking = sessionsMinusMakeups?.[index]
              return (
                <SessionBox
                  session={booking}
                  coachingGroup={info.row.original.coachingGroup}
                />
              )
            })}
            {(i === 0 || makeupSessions.length > 0) && (
              <div
                style={{
                  width: "1px",
                  height: "32px",
                  minHeight: "32px",
                  backgroundColor: JoonUIColor.border.default,
                  margin: "0 4px",
                }}
              />
            )}
          </>
        )
      })}
      {makeupSessions.map((session) => (
        <SessionBox
          session={session}
          coachingGroup={info.row.original.coachingGroup}
        />
      ))}
    </FlexBox>
  )
}

export default SessionsCell

const SessionBox = ({
  session,
  coachingGroup,
}: {
  session: SessionBooking | undefined
  coachingGroup: CoachingGroup | undefined
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const isBooked = !!session
  const isNoShow = !!session?.marked_no_show
  const isLateCancellation = !!session?.marked_late_cancellation
  const makeupReason = session?.makeup_reason
  const isMakeupSession = !!session?.missed_session_id
  const tooltipId = useId()
  const isInPast = dayjs(session?.end_datetime).isBefore(dayjs())
  const hasSessionNotes = !!session?.session_notes?.note

  const navigate = useNavigate()
  const { onOpen: openSessionModal } = useSessionModalStore()
  const getSessionData = () => {
    if (!isBooked) {
      return {
        icon: null,
        tooltip: "Not yet scheduled",
        backgroundColor: JoonColorExpanded.neutral100,
      }
    } else if (isNoShow) {
      return {
        icon: <BanIcon />,
        tooltip: `No show${makeupReason ? ` (${makeupReason})` : ""}`,
        backgroundColor: JoonColorExpanded.red100,
      }
    } else if (isLateCancellation) {
      return {
        icon: <BanIcon />,
        tooltip: `Late cancellation ${makeupReason ? `(${makeupReason})` : ""}`,
        backgroundColor: JoonColorExpanded.red100,
      }
    } else if (
      isInPast &&
      !hasSessionNotes &&
      !isNoShow &&
      !isLateCancellation
    ) {
      return {
        icon: <NoNotesIcon />,
        tooltip: "Missing notes",
        backgroundColor: JoonColorExpanded.orange100,
      }
    } else if (isInPast && hasSessionNotes) {
      return {
        icon: <CheckIcon color={JoonUIColor.icon.neutral} size={16} />,
        tooltip: "Notes submitted",
        backgroundColor: JoonColorExpanded.neutral100,
      }
    } else if (isMakeupSession) {
      return {
        icon: <CalendarCheckIcon color={JoonUIColor.icon.neutral} size={16} />,
        tooltip: "Makeup session",
        backgroundColor: JoonColorExpanded.viridian100,
      }
    } else {
      return {
        icon: <CalendarCheckIcon color={JoonUIColor.icon.neutral} size={16} />,
        tooltip: "Session scheduled",
        backgroundColor: JoonColorExpanded.neutral100,
      }
    }
  }

  const { icon, tooltip, backgroundColor } = getSessionData()

  const sessionDate = session?.start_datetime
    ? dayjs(session?.start_datetime).format("MMM D")
    : undefined

  const viewSession = () => {
    if (!isBooked) return
    openSessionModal(session?.id)
    navigate(`/${coachingGroup?.id}/${ClientTab.SESSIONS}`)
  }

  return (
    <>
      <FlexBox
        align="center"
        justify="center"
        style={{
          minWidth: SPACING.space6,
          width: SPACING.space6,
          minHeight: SPACING.space6,
          height: SPACING.space6,
          background: backgroundColor,
          borderRadius: SPACING.space1,
          border: isHovered
            ? `1px solid ${JoonUIColor.border.accent}`
            : "1px solid transparent",
          cursor: isBooked ? "pointer" : "default",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={viewSession}
        data-tooltip-id={tooltipId}
      >
        {icon}
      </FlexBox>
      <Tooltip id={tooltipId}>
        <>
          {sessionDate ? (
            <Typography
              variant="bodySmall"
              textAlign="left"
              color={JoonUIColor.text.inverted}
            >
              {sessionDate || ""}
            </Typography>
          ) : null}
          <Typography
            variant="bodySmall"
            color={JoonUIColor.text.inverted}
            textAlign="left"
          >
            {tooltip}
          </Typography>
        </>
      </Tooltip>
    </>
  )
}

export const NoNotesIcon = ({
  size = 20,
  color = "#DD7B46",
}: {
  size?: number
  color?: string
}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 15"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="2.40629" cy="12.6883" r="1.47783" fill="#DD7B46" />
      <circle cx="6.41093" cy="12.6883" r="1.47783" fill="#DD7B46" />
      <circle cx="10.4156" cy="12.6883" r="1.47783" fill="#DD7B46" />
      <g clip-path="url(#clip0_5813_1560)">
        <g clip-path="url(#clip1_5813_1560)">
          <path
            d="M20.7014 3.58371L20.1125 4.86063L16.6827 3.27883L17.2717 2.00191C17.5758 1.34235 18.3565 1.0545 19.0161 1.35869L20.0575 1.83739C20.717 2.14158 21.0049 2.92222 20.7007 3.58179L20.7014 3.58371ZM19.8375 5.45688L16.9993 11.6074C16.8727 11.8817 16.6781 12.1199 16.4362 12.3011L14.369 13.8385C14.2229 13.9471 14.0312 13.9674 13.8676 13.892C13.704 13.8165 13.595 13.6575 13.5816 13.4785L13.4089 10.9081C13.3896 10.6065 13.4444 10.3038 13.571 10.0294L16.4078 3.87508L19.8375 5.45688Z"
            fill={color}
          />
        </g>
      </g>
      <defs>
        <clipPath id="clip0_5813_1560">
          <rect
            width="10.5184"
            height="10.5184"
            fill="white"
            transform="matrix(-0.345966 -0.938247 -0.938247 0.345966 23.6512 10.438)"
          />
        </clipPath>
        <clipPath id="clip1_5813_1560">
          <rect
            width="10.5184"
            height="10.5184"
            fill="white"
            transform="matrix(-0.345966 -0.938247 -0.938247 0.345966 23.6512 10.438)"
          />
        </clipPath>
      </defs>
    </svg>
  )
}
